import { useCallback, useEffect, useState } from 'react'
import { aiSettingsApi, type AiSettings } from '../api/aiSettings'
import { Button, Callout, Card, CardContent, CardHeader, StatusPill, TextField } from '../components/ui'

/**
 * Chiave Anthropic per la lettura documenti con IA (solo Admin). Viene salvata cifrata e non torna mai al browser:
 * qui si vede solo se c'e', il modello usato e quando e' stata cambiata.
 */
export function AiSettingsPanel() {
  const [state, setState] = useState<AiSettings | null>(null)
  const [hidden, setHidden] = useState(false)
  const [apiKey, setApiKey] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null)

  const load = useCallback(async () => {
    try {
      setState(await aiSettingsApi.get())
    } catch {
      setHidden(true) // non Admin
    }
  }, [])

  useEffect(() => { void load() }, [load])

  if (hidden) return null

  const save = async () => {
    setBusy(true); setMessage(null)
    try {
      await aiSettingsApi.save(apiKey.trim())
      setApiKey('')
      setMessage({ tone: 'success', text: 'Chiave salvata.' })
      await load()
    } catch (e) {
      setMessage({ tone: 'error', text: e instanceof Error ? e.message : 'Salvataggio non riuscito.' })
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    setBusy(true); setMessage(null)
    try {
      await aiSettingsApi.remove()
      setMessage({ tone: 'success', text: 'Chiave rimossa.' })
      await load()
    } catch (e) {
      setMessage({ tone: 'error', text: e instanceof Error ? e.message : 'Rimozione non riuscita.' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card variant="outlined" padding="none">
      <CardHeader title="IA – Lettura documenti" />
      <CardContent>
        {!state ? (
          <p className="text-muted md-typescale-body-small mb-0">Caricamento…</p>
        ) : (
          <>
            <p className="md-typescale-body-small mb-2">
              {state.configured
                ? <StatusPill tone="success">Chiave impostata</StatusPill>
                : <StatusPill tone="neutral">Nessuna chiave</StatusPill>}
              <span className="text-muted ms-2">Modello {state.model}</span>
            </p>
            {state.updatedAt && (
              <p className="text-muted md-typescale-body-small mb-2">
                Aggiornata il {new Date(state.updatedAt).toLocaleString('it-IT')}
              </p>
            )}
            {!state.encryptionAvailable ? (
              <Callout tone="warning">La cifratura dei segreti non è attiva sul server: la chiave non si può salvare.</Callout>
            ) : (
              <>
                <TextField label="Chiave API Anthropic" type="password" autoComplete="off" value={apiKey}
                  onChange={e => setApiKey(e.target.value)}
                  supportingText="Si salva cifrata e non viene più mostrata." />
                <div className="d-flex gap-2 mt-2">
                  <Button variant="filled" size="sm" disabled={busy || apiKey.trim() === ''} onClick={() => void save()}>
                    Salva
                  </Button>
                  {state.configured && (
                    <Button variant="outlined" size="sm" disabled={busy} onClick={() => void remove()}>Rimuovi</Button>
                  )}
                </div>
              </>
            )}
            {message && <Callout tone={message.tone} className="mt-2">{message.text}</Callout>}
            <p className="text-muted md-typescale-body-small mt-2 mb-0">
              Per usarla attiva anche la funzione <code>documenti.lettura_automatica</code>.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  )
}
